import React from 'react';
import {ColoredTypography, Section} from './CardStyles';
import {COLORS} from '@fupa/fupa-uikit';
import ContentImage from '../components/ContentImage';
import styled from 'styled-components';
import {motion} from 'framer-motion';
import {createVariants} from '../motions/MotionVariants';

const Wrapper = styled('div')`
  display: grid;
  gap: 4.5rem;
`;

const ContainerTextAndImage = styled('div')`
  display: flex;
  max-width: 1200px;
  flex-direction: row;
  padding: 0 2rem;
  align-items: center;
  justify-content: space-between;
  gap: 32px;

  @media all and (max-width: 800px) {
    flex-direction: column;
  }
`;

const ImageWrapper = styled('div')`
  display: flex;
  justify-content: center;
  flex: 1;
`;

const DoubleImageContainer = styled(ImageWrapper)`
  gap: 1rem;
  margin: auto;
  align-items: center;
`;

const TextContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  text-align: left;
  width: 60%;

  @media all and (max-width: 800px) {
    width: 100%;
  }
`;

interface ImgHeight {
  mobile: number;
  desktop: number;
}

const imgHeight: Record<string, ImgHeight> = {
  squad: {
    mobile: 300,
    desktop: 400,
  },
  standing: {
    mobile: 300,
    desktop: 250,
  },
  stats: {
    mobile: 300,
    desktop: 250,
  },
  matches: {
    mobile: 300,
    desktop: 400,
  },
  liveticker: {
    mobile: 300,
    desktop: 400,
  },
};
const imgWidth: Record<string, ImgHeight> = {
  squad: {
    mobile: 150,
    desktop: 200,
  },
  standing: {
    mobile: 483,
    desktop: 402,
  },
  stats: {
    mobile: 516,
    desktop: 423,
  },
  matches: {
    mobile: 150,
    desktop: 200,
  },
  liveticker: {
    mobile: 150,
    desktop: 200,
  },
};

const SamplesCard = () => {
  const getImgHeight = (type: string) =>
    window.matchMedia('(max-width: 800px)').matches ? imgHeight[type].mobile : imgHeight[type].desktop;
  const getImgWidth = (type: string) =>
    window.matchMedia('(max-width: 800px)').matches ? imgWidth[type].mobile : imgWidth[type].desktop;

  return (
    <>
      <Section backgroundColor={COLORS.lighterGrey}>
        <Wrapper>
          <ContainerTextAndImage>
            <ImageWrapper>
              <ContentImage
                comesInViewFrom={'left'}
                lazy={true}
                imgHeight={getImgHeight('standing')}
                imgWidth={getImgWidth('standing')}
                imgAlt='Darstellung des Tabellen Widgets'
                imgSrc={'/landingpage_samples_standing_devices'}
              />
            </ImageWrapper>
            <TextContainer
              initial='offscreen'
              whileInView='onscreen'
              variants={createVariants('right')}
              viewport={{amount: 0.5, once: true}}>
              <ColoredTypography variant='headline2' component='h2'>
                Tabellen Widget
              </ColoredTypography>
              <ColoredTypography variant='subheading' component='p'>
                Das Tabellen Widget zeigt die gesamte Liga-Tabelle der Saison, in der das Team aktiv spielt.
                <br />
                Hierbei sind alle wichtigen Informationen auf einen Blick ersichtlich: Spiele, Siege, Unentschieden,
                Niederlagen, Tore, Gegentore, Tordifferenz und Punkte. <br />
                Falls dein Platz auf der Seite begrenzt ist, kannst du ganz einfach die sichtbaren Spalten nach deinem
                individuellen Bedarf während der Erstellung des Widgets anpassen.
                <br /> Zudem werden Aufstiegs-, Abstiegs- und Relegationsplätze in der Tabelle hervorgehoben.
              </ColoredTypography>
            </TextContainer>
          </ContainerTextAndImage>
          <ContainerTextAndImage>
            <DoubleImageContainer>
              <ImageWrapper>
                <ContentImage
                  comesInViewFrom={'left'}
                  lazy={true}
                  imgHeight={getImgHeight('squad')}
                  imgWidth={getImgWidth('squad')}
                  imgAlt='Darstellung des Kader Widgets'
                  imgSrc={'/landingpage_kader_mobile'}
                />
              </ImageWrapper>
              <ImageWrapper>
                <ContentImage
                  comesInViewFrom={'left'}
                  lazy={true}
                  imgHeight={getImgHeight('matches')}
                  imgWidth={getImgWidth('matches')}
                  imgAlt='Darstellung des Spielplan Widgets'
                  imgSrc={'/landingpage_matches_mobile'}
                />
              </ImageWrapper>
            </DoubleImageContainer>
            <TextContainer
              initial='offscreen'
              whileInView='onscreen'
              variants={createVariants('right')}
              viewport={{amount: 0.5, once: true}}>
              <ColoredTypography variant='headline2' component='h2'>
                Kader Widget
              </ColoredTypography>
              <ColoredTypography variant='subheading' component='p'>
                Mit unserem Kader Widget präsentierst du die wichtigsten Informationen zu den Spielern des Teams — ihre
                Bilder, Namen, Positionen und Rückennummern.
                <br />
                Selbstverständlich sind auch kurze Zusammenfassungen der aktuellen Statistiken unverzichtbar,
                einschließlich Einsätze, Tore und Assists. <br />
                Des Weiteren werden Spieler besonders hervorgehoben, wenn es sich um Neuzugänge handelt oder sie
                gesperrt oder verletzt sind.
              </ColoredTypography>
              <ColoredTypography variant='headline2' component='h2'>
                Spielplan Widget
              </ColoredTypography>
              <ColoredTypography variant='subheading' component='p'>
                Mit unsererem Spielplan Widget erhältst du einen umfassenden Überblick über alle Spiele eines Teams in
                der aktuellen Saison. <br />
                Dabei können die verschiedenen Spielkategorien, darunter Ligaspiele, Turniere, Pokalspiele, Testspiele
                und Relegationsspiele, je nach Bedarf bei der Erstellung konfiguriert werden.
                <br /> Die Sortierung erfolgt automatisch nach Datum.
                <br />
              </ColoredTypography>
            </TextContainer>
          </ContainerTextAndImage>
          <ContainerTextAndImage>
            <ImageWrapper>
              <ContentImage
                comesInViewFrom={'left'}
                lazy={true}
                imgHeight={getImgHeight('stats')}
                imgWidth={getImgWidth('stats')}
                imgAlt='Darstellung des Spielerstatistik Widgets'
                imgSrc={'/landingpage_stats_desktop'}
              />
            </ImageWrapper>
            <TextContainer
              initial='offscreen'
              whileInView='onscreen'
              variants={createVariants('right')}
              viewport={{amount: 0.5, once: true}}>
              <ColoredTypography variant='headline2' component='h2'>
                Spielerstatistik Widget
              </ColoredTypography>
              <ColoredTypography variant='subheading' component='p'>
                Das Spielerstatistik Widget gibt dir die Möglichkeit die aktuellen Spieler des Teams zusammen mit ihren
                umfassenden Statistiken für die laufende Saison zu zeigen.
                <br /> Die standardmäßige Rangliste ergibt sich gemäß den Einsätzen, doch diese lässt sich jederzeit
                interaktiv nach jeder anderen Statistik sortieren. <br />
                Wie bereits beim Tabellen Widget besteht auch hier die Möglichkeit, die anzuzeigenden Spalten nach
                individuellem Bedarf bei der Erstellung anzupassen.
                <br />
              </ColoredTypography>
            </TextContainer>
          </ContainerTextAndImage>
          <ContainerTextAndImage>
            <DoubleImageContainer>
              <ImageWrapper>
                <ContentImage
                  comesInViewFrom={'left'}
                  lazy={true}
                  imgHeight={getImgHeight('liveticker')}
                  imgWidth={getImgWidth('liveticker')}
                  imgAlt='Darstellung des Liveticker Widgets'
                  imgSrc={'/landingpage_ticker_mobile'}
                />
              </ImageWrapper>
              <ImageWrapper>
                <ContentImage
                  comesInViewFrom={'left'}
                  lazy={true}
                  imgHeight={getImgHeight('liveticker')}
                  imgWidth={getImgWidth('liveticker')}
                  imgAlt='Darstellung der Teaser'
                  imgSrc={'/landingpage_teaser_mobile'}
                />
              </ImageWrapper>
            </DoubleImageContainer>
            <TextContainer
              initial='offscreen'
              whileInView='onscreen'
              variants={createVariants('right')}
              viewport={{amount: 0.5, once: true}}>
              <ColoredTypography variant='headline2' component='h2'>
                Liveticker Widget
              </ColoredTypography>
              <ColoredTypography variant='subheading' component='p'>
                Mehr "Live" geht nicht! Mit dem Liveticker Widget informierst du deine Fans in Echtzeit - mit
                ausführlichen Schilderungen zum Spielgeschehen oder Live-Zwischenständen deiner Teams. Ganz wie du
                möchtest! Das Widget zeigt alle wichtigen Ereignisse der Partie, sofern die Partie live durch einen
                FuPa-Tickermelder begleitet wird.
                <br />
                Das Widget muss dazu nur einmalig eingebunden werden und wechselt immer automatisch zum aktuellen Spiel.
              </ColoredTypography>
              <ColoredTypography variant='headline2' component='h2'>
                Tabelle, Spielplan und Liveticker auch als kompakter Teaser verfügbar
              </ColoredTypography>
              <ColoredTypography variant='subheading' component='p'>
                Für einen perfekten Überblick - sowohl desktop als auch mobil - bieten wir dir mit unseren kompakten
                Teaser-Ansichten die Möglichkeit bei den Widgets "Tabelle, Spielplan und Liveticker" das Layout auf
                einen Teaser umzustellen.
                <br /> Dieses Widget ist vor allem für die Homepage-Startseiten oder Übersichts-Seiten gedacht. Um -
                ohne viel Platz zu verbrauchen - gleichzeitig die Tabellen / Spielpläne / Liveticker verschiedener Teams
                nebeneinander zu platzieren und deinen Fans einen optimalen Überblick über's aktuelle Vereinsgeschehen
                zu geben.
                <br />
              </ColoredTypography>
            </TextContainer>
          </ContainerTextAndImage>
        </Wrapper>
      </Section>
    </>
  );
};

export default SamplesCard;
